import React from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { fmtMoney } from "@/lib/format";
import { PageHeader, SectionTitle, AwaitingData } from "@/components/common/Page";
import { KpiCard } from "@/components/common/KpiCard";
import { DataTable } from "@/components/common/DataTable";
import { Loader2 } from "lucide-react";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";

const chartTip = { backgroundColor: "#111A2E", border: "1px solid #1E2D4A", borderRadius: 8, color: "#F8FAFC", fontSize: 12 };

export default function CashFlow() {
  const { data, isLoading } = useQuery({ queryKey: ["cashflow"], queryFn: async () => (await api.get("/finance/cashflow")).data });
  if (isLoading) return <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-cyan-400" /></div>;

  const series = (data.series || []).map((s) => ({ ...s, label: s.month.slice(2) }));
  const hasFlow = series.some((s) => s.received || s.paid);

  const columns = [
    { key: "month", header: "Month" },
    { key: "opening", header: "Opening", align: "right", mono: true, render: (r) => fmtMoney(r.opening) },
    { key: "received", header: "Received", align: "right", mono: true, render: (r) => fmtMoney(r.received) },
    { key: "paid", header: "Paid", align: "right", mono: true, render: (r) => fmtMoney(r.paid) },
    { key: "net", header: "Net", align: "right", mono: true, render: (r) => <span className={r.net >= 0 ? "text-emerald-400" : "text-rose-400"}>{fmtMoney(r.net)}</span> },
    { key: "closing", header: "Closing", align: "right", mono: true, render: (r) => fmtMoney(r.closing) },
  ];

  return (
    <div className="ns-fade">
      <PageHeader title="Cash Flow" subtitle="Cash position, monthly flow and runway" testId="cashflow-header" />
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <KpiCard testId="cash-opening" label="Opening Cash" value={fmtMoney(data.opening_cash)} accent="slate" />
        <KpiCard testId="cash-current" label="Current Cash" value={fmtMoney(data.current_cash)} accent="cyan" />
        <KpiCard testId="cash-receivables" label="Expected Receivables" value={fmtMoney(data.expected_receivables)} accent="emerald" />
        <KpiCard testId="cash-runway" label="Cash Runway" value={data.runway.months != null ? `${data.runway.months} mo` : "∞"} sub={data.runway.note || `${data.alert_threshold}-mo threshold`} accent={data.runway.months != null && data.runway.months < data.alert_threshold ? "rose" : "emerald"} />
      </div>

      <SectionTitle className="mb-3 mt-8">12-Month Cash Position</SectionTitle>
      <div className="rounded-xl border border-[#1E2D4A] bg-[#111A2E]/70 p-5">
        {hasFlow ? (
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={series}>
              <defs>
                <linearGradient id="cashGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#22D3EE" stopOpacity={0.4} />
                  <stop offset="100%" stopColor="#22D3EE" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#1E2D4A" vertical={false} />
              <XAxis dataKey="label" stroke="#64748B" fontSize={11} />
              <YAxis stroke="#64748B" fontSize={11} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
              <Tooltip contentStyle={chartTip} formatter={(v) => fmtMoney(v)} />
              <Area type="monotone" dataKey="closing" name="Closing Cash" stroke="#22D3EE" strokeWidth={2} fill="url(#cashGrad)" />
            </AreaChart>
          </ResponsiveContainer>
        ) : (
          <div className="flex h-[280px] flex-col items-center justify-center">
            <AwaitingData label="No cash movements yet" />
            <p className="mt-2 max-w-sm text-center text-xs text-slate-500">Mark revenue and expenses as paid to build the cash-flow projection.</p>
          </div>
        )}
      </div>

      <SectionTitle className="mb-3 mt-8">Monthly Detail</SectionTitle>
      <DataTable columns={columns} rows={series} testId="cashflow-table" empty={{ title: "No data" }} />
    </div>
  );
}
